import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import CcmPanel from './CcmPanel';
import PendingSignaturesCard from './PendingSignaturesCard';
import { mergedVendorCount } from '../Vendors/Vendors';
import TscHeatmap from './TscHeatmap';
import OnboardingWizard from './OnboardingWizard';
import IsmsHomeCard from '../Isms/IsmsHomeCard';
import './UserHome.css';
import { rememberTestOrg } from '../../utils/testOrg';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(date) {
  const secs = Math.floor((new Date() - new Date(date)) / 1000);
  if (secs < 60)    return i18n.t('UserHome.timeAgoSeconds', { count: secs });
  if (secs < 3600)  return i18n.t('UserHome.timeAgoMinutes', { count: Math.floor(secs / 60) });
  if (secs < 86400) return i18n.t('UserHome.timeAgoHours', { count: Math.floor(secs / 3600) });
  return i18n.t('UserHome.timeAgoDays', { count: Math.floor(secs / 86400) });
}

function complianceStatus(pct) {
  if (pct >= 80) return { color: '#16a34a', bg: '#dcfce7', label: i18n.t('UserHome.statusOnTrack') };
  if (pct >= 60) return { color: '#d97706', bg: '#fef3c7', label: i18n.t('UserHome.statusInProgress') };
  if (pct >= 40) return { color: '#ea580c', bg: '#ffedd5', label: i18n.t('UserHome.statusNeedsWork') };
  return           { color: '#dc2626', bg: '#fee2e2', label: i18n.t('UserHome.statusAtRisk') };
}

// ─── Posture Hero ─────────────────────────────────────────────────────────────
function PostureHero({ score, testScore, passedTests, totalTests, failedCount, atRiskCount }) {
  const { t } = useTranslation();
  const pct  = score || 0;
  const r    = 64;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const tone = pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';

  return (
    <div className="uh-ph">
      <div className="uh-ph-ring-wrap">
        <svg width="150" height="150" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="75" cy="75" r={r} stroke="#f1f5f9" strokeWidth="11" fill="none" />
          <circle cx="75" cy="75" r={r} stroke={tone} strokeWidth="11" fill="none"
                  strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.4s ease' }} />
        </svg>
        <div className="uh-ph-ring-center">
          <span className="uh-ph-ring-eyebrow" title={t('UserHome.postureTooltip')}>{t('UserHome.postureEyebrow')}</span>
          <span className="uh-ph-ring-score" style={{ color: tone }}>{pct}</span>
          <span className="uh-ph-ring-sub">{t('UserHome.outOf100')}</span>
        </div>
      </div>

      <div className="uh-ph-content">
        <div className="uh-ph-eyebrow">{t('UserHome.complianceOverview')}</div>
        <h2 className="uh-ph-heading">
          {failedCount > 0
            ? <>{t('UserHome.youHave')} <span style={{ color: '#dc2626' }}>{t('UserHome.failingTestsCount', { count: failedCount })}</span>
                {atRiskCount > 0 ? t('UserHome.acrossFrameworks', { count: atRiskCount }) : ''}.
              </>
            : <><span style={{ color: '#16a34a' }}>{t('UserHome.allTestsPassing')}</span> {t('UserHome.greatShape')}</>
          }
        </h2>
        <p className="uh-ph-desc">
          {testScore > 0
            ? t('UserHome.automatedTestsPassing', { testScore })
            : t('UserHome.runComplianceTestsHint')
          }
        </p>
        <div className="uh-ph-stats">
          <div className="uh-ph-stat">
            <span className="uh-ph-stat-lbl">{t('UserHome.testsPassing')}</span>
            <b className="uh-ph-stat-val">{t('UserHome.xOfY', { passed: passedTests, total: totalTests })}</b>
          </div>
          <div className="uh-ph-stat">
            <span className="uh-ph-stat-lbl">{t('UserHome.testScore')}</span>
            <b className="uh-ph-stat-val">{testScore || 0}%</b>
          </div>
          <div className="uh-ph-stat">
            <span className="uh-ph-stat-lbl">{t('UserHome.failingTests')}</span>
            <b className="uh-ph-stat-val" style={{ color: failedCount > 0 ? '#dc2626' : '#16a34a' }}>{failedCount}</b>
          </div>
          <div className="uh-ph-stat">
            <span className="uh-ph-stat-lbl">{t('UserHome.frameworksAtRisk')}</span>
            <b className="uh-ph-stat-val" style={{ color: atRiskCount > 0 ? '#ea580c' : '#16a34a' }}>
              {atRiskCount > 0 ? atRiskCount : t('UserHome.none')}
            </b>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Strip ────────────────────────────────────────────────────────────────
function KPIStrip({ totalFailed, vendors, assets, vulns }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const TONES = {
    critical: { c: '#dc2626', bg: '#fef2f2' },
    warn:     { c: '#d97706', bg: '#fffbeb' },
    ok:       { c: '#16a34a', bg: '#f0fdf4' },
    neutral:  { c: '#1e293b', bg: '#f8fafc' },
  };
  const cells = [
    {
      label: t('UserHome.kpiFailingTests'), value: totalFailed,
      tone: totalFailed > 0 ? 'critical' : 'ok',
      icon: 'fa-flask',
      sub: totalFailed > 0 ? t('UserHome.kpiOfRun', { count: totalFailed + (/* passed */ 0) }) : t('UserHome.kpiAllPassing'),
      to: '/dashboard/tests?status=FAIL',
    },
    {
      label: t('UserHome.kpiVendors'), value: vendors,
      tone: 'neutral',
      icon: 'fa-store',
      sub: t('UserHome.kpiThirdPartyServices'),
      to: '/dashboard/vendors',
    },
    {
      label: t('UserHome.kpiAssets'), value: assets,
      tone: 'ok',
      icon: 'fa-server',
      sub: t('UserHome.kpiMonitored'),
      to: '/dashboard/assets',
    },
    {
      label: t('UserHome.kpiOpenVulns'), value: vulns,
      tone: vulns > 0 ? 'critical' : 'ok',
      icon: 'fa-bug',
      sub: vulns === 0 ? t('UserHome.kpiAllClear') : t('UserHome.kpiOpen', { count: vulns }),
      to: '/dashboard/iasm',
    },
  ];

  return (
    <div className="uh-kpi-strip">
      {cells.map((k, i) => {
        const t = TONES[k.tone];
        return (
          <div key={i} className="uh-kpi-strip-cell" onClick={() => navigate(k.to)}>
            <div className="uh-kpi-strip-row">
              <span className="uh-kpi-strip-lbl">{k.label}</span>
              <div className="uh-kpi-strip-icon" style={{ background: t.bg, color: t.c }}>
                <i className={`fas ${k.icon}`} />
              </div>
            </div>
            <div className="uh-kpi-strip-val" style={{ color: t.c }}>{k.value}</div>
            <div className="uh-kpi-strip-sub">{k.sub}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Focus Card ───────────────────────────────────────────────────────────────
function FocusCard({ test }) {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const ageHrs   = test.failedDate ? (Date.now() - new Date(test.failedDate)) / 3_600_000 : 999;
  const sev      = ageHrs < 24 ? 'critical' : ageHrs < 72 ? 'high' : 'medium';
  const TONES    = {
    critical: { c: '#dc2626', bg: '#fef2f2', border: '#fecaca', solid: '#dc2626' },
    high:     { c: '#ea580c', bg: '#fff7ed', border: '#fed7aa', solid: '#ea580c' },
    medium:   { c: '#d97706', bg: '#fffbeb', border: '#fde68a', solid: '#d97706' },
  };
  const t = TONES[sev];

  return (
    <div
      className="uh-fcard"
      style={{ borderColor: t.border, borderLeftColor: t.solid }}
      onClick={() => navigate(`/dashboard/tests/${test.id}`)}
    >
      <div className="uh-fcard-top">
        <div className="uh-fcard-icon" style={{ background: t.bg, color: t.c }}>
          <i className="fas fa-shield-alt" />
        </div>
        <div className="uh-fcard-body">
          <div className="uh-fcard-badges">
            <span className="uh-fcard-sev" style={{ background: t.solid }}>{translate(`UserHome.severity_${sev}`)}</span>
            {test.failedDate && (
              <span className="uh-fcard-age">
                <i className="fas fa-clock" /> {timeAgo(test.failedDate)}
              </span>
            )}
          </div>
          <div className="uh-fcard-title">{test.testName}</div>
        </div>
      </div>
      <div className="uh-fcard-footer">
        <span className="uh-fcard-type">{translate('UserHome.complianceTest')}</span>
        <button
          className="uh-fcard-btn"
          style={{ background: t.solid }}
          onClick={e => { e.stopPropagation(); navigate(`/dashboard/tests/${test.id}`); }}
        >
          {translate('UserHome.view')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

// ─── Framework Block ──────────────────────────────────────────────────────────
function FrameworkBlock({ name, pct, fwId }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rounded  = Math.round(pct || 0);
  const s        = complianceStatus(rounded);

  return (
    <div
      className="uh-fwblock"
      onClick={() => fwId
        ? navigate(`/dashboard/compliance/frameworks/${fwId}/requirements`)
        : navigate('/dashboard/compliance/frameworks')
      }
    >
      <div className="uh-fwblock-top">
        <div className="uh-fwblock-name">{name}</div>
        <span className="uh-fwblock-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>
      </div>
      <div className="uh-fwblock-pct" style={{ color: s.color }}>
        {rounded}<span className="uh-fwblock-unit">%</span>
        <span className="uh-fwblock-ready-lbl">{t('UserHome.ready')}</span>
      </div>
      <div className="uh-fw-bar-bg">
        <div className="uh-fw-bar-fill" style={{ width: `${rounded}%`, background: s.color }} />
      </div>
      <div className="uh-fwblock-footer">
        <span className="uh-fwblock-hint">{t('UserHome.viewRequirements')}</span>
        <i className="fas fa-arrow-right uh-fwblock-arrow" />
      </div>
    </div>
  );
}

// ─── Pillar Grid (amalgamated posture: people · assets · governance · attack surface) ───
function PillarGrid({ posture }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!posture) return null;
  const ppl = posture.people || {};        const pt  = ppl.byType || {};
  const ast = posture.assets || {};        const ap  = ast.byProvider || {};
  const gov = posture.governance || {};
  const as  = posture.attackSurface || {}; const sev = as.bySeverity || {};

  const pillars = [
    { title: t('UserHome.pillarPeople'), icon: 'fa-users', to: '/dashboard/people',
      total: ppl.total || 0, sub: t('UserHome.pillarPeopleSub'),
      chips: [[t('UserHome.pillarChipHuman'), pt.HUMAN || 0], [t('UserHome.pillarChipService'), pt.SERVICE || 0], [t('UserHome.pillarChipRole'), pt.ROLE || 0]] },
    { title: t('UserHome.pillarAssets'), icon: 'fa-server', to: '/dashboard/assets',
      total: ast.total || 0, sub: t('UserHome.pillarAssetsSub'),
      chips: Object.entries(ap).slice(0, 3) },
    { title: t('UserHome.pillarGovernance'), icon: 'fa-file-contract', to: '/dashboard/compliance/policies',
      total: (gov.policiesPublished || 0) + (gov.documentsInForce || 0), sub: t('UserHome.pillarGovernanceSub'),
      chips: [[t('UserHome.pillarChipPolicies'), `${gov.policiesPublished || 0}/${gov.policiesTotal || 0}`],
              [t('UserHome.pillarChipDocuments'), `${gov.documentsInForce || 0}/${gov.documentsTotal || 0}`]] },
    { title: t('UserHome.pillarAttackSurface'), icon: 'fa-bug', to: '/dashboard/iasm',
      total: as.openTotal || 0, sub: t('UserHome.pillarAttackSurfaceSub'), danger: (as.openTotal || 0) > 0,
      chips: [[t('UserHome.pillarChipCritical'), sev.CRITICAL || 0], [t('UserHome.pillarChipHigh'), sev.HIGH || 0], [t('UserHome.pillarChipMedium'), sev.MEDIUM || 0]] },
  ];

  return (
    <section className="uh-section">
      <div className="uh-section-hdr">
        <div className="uh-section-title" style={{ marginBottom: 0 }}>{t('UserHome.postureAcrossProgram')}</div>
        <span className="uh-meta-note">{t('UserHome.postureMetaNote')}</span>
      </div>
      <div className="uh-pillar-grid">
        {pillars.map(p => (
          <div key={p.title} className="uh-pillar" onClick={() => navigate(p.to)}>
            <div className="uh-pillar-top">
              <div className="uh-pillar-icon"><i className={`fas ${p.icon}`} /></div>
              <span className="uh-pillar-title">{p.title}</span>
              <i className="fas fa-arrow-right uh-pillar-arrow" />
            </div>
            <div className="uh-pillar-total" style={p.danger ? { color: '#dc2626' } : undefined}>
              {p.total}<span className="uh-pillar-sub"> {p.sub}</span>
            </div>
            <div className="uh-pillar-chips">
              {p.chips.map(([k, v]) => (
                <span key={k} className="uh-pillar-chip">{k} <b>{v}</b></span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── UserHome ─────────────────────────────────────────────────────────────────
function UserHome() {
  const _owUser = localStorage.getItem('userName') || localStorage.getItem('username') || 'user';
  const [wizardDismissed, setWizardDismissed] = useState(!!localStorage.getItem(`onboardingCompleted_${_owUser}`));
  const [dashboardData, setDashboardData] = useState(null);
  const [failedTests, setFailedTests]     = useState([]);
  const [vendorCount, setVendorCount]     = useState(0);
  const [orgFrameworks, setOrgFrameworks] = useState([]);
  const [readiness, setReadiness]         = useState(null);
  const [posture, setPosture]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const load = async () => {
      try {
        const token    = localStorage.getItem('authToken');
        const userName = localStorage.getItem('userName');
        const headers  = { Authorization: `Bearer ${token}` };

        const [dashRes, alertsRes, userRes, readinessRes, postureRes, vendorsRes, connectionsRes] = await Promise.all([
          axiosInstance.get('/dashboard', { headers }),
          axiosInstance.get('/audit/latest-failed-tests-alert', { headers }),
          axiosInstance.get(`/users/username/${userName}`, { headers }),
          axiosInstance.get('/audit/readiness-score', { headers }).catch(() => ({ data: null })),
          axiosInstance.get('/dashboard/posture', { headers }).catch(() => ({ data: null })),
          axiosInstance.get('/vendors', { headers }).catch(() => ({ data: [] })),
          axiosInstance.get('/vendors/connections', { headers }).catch(() => ({ data: [] })),
        ]);

        setDashboardData(dashRes.data);
        setFailedTests(alertsRes.data || []);
        // Same merged count (Vendor + VendorConnection) the Vendors page shows, so the KPI matches it.
        setVendorCount(mergedVendorCount(vendorsRes.data, connectionsRes.data));
        setOrgFrameworks(userRes.data?.organization?.frameworks || []);
        // Refresh the test-org mirror from the profile this page already fetches, so a tag applied
        // after login lands on the next dashboard visit rather than only on re-login.
        rememberTestOrg(userRes.data);
        setReadiness(readinessRes.data);
        setPosture(postureRes.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="uh-loading"><div className="uh-spinner" /></div>;
  if (error)   return <div className="uh-error">{t('UserHome.errorLoadingDashboard', { error })}</div>;

  const cd           = dashboardData?.complianceData || {};
  const frameworkMap = Object.fromEntries(orgFrameworks.map(f => [f.displayName, f.id]));
  const totalTests   = dashboardData?.totalTests       || 0;
  const passedTests  = dashboardData?.totalPassedTests || 0;
  const failedCount  = dashboardData?.totalFailedTests || 0;
  const vulns        = posture?.attackSurface?.openTotal || 0;
  const atRiskCount  = Object.values(cd).filter(p => p < 80).length;
  // An org with test history is already established (enrolled + scanned before) — the setup
  // wizard is for brand-new orgs, so a per-browser localStorage flag alone re-shows it to any
  // returning admin on a new device/incognito session for an org that's months into real use.
  const isEstablishedOrg = totalTests > 0;
  return (
    <div className="uh-page">
      {!wizardDismissed && !isEstablishedOrg &&
        <OnboardingWizard onComplete={() => setWizardDismissed(true)} />}

      {/* ── Posture Hero ──────────────────────────────── */}
      <div className="uh-section">
        <PostureHero
          score={readiness?.score}
          testScore={readiness?.testScore}
          passedTests={passedTests}
          totalTests={totalTests}
          failedCount={failedCount}
          atRiskCount={atRiskCount}
        />
      </div>

      {/* ── KPI Strip ─────────────────────────────────── */}
      <KPIStrip
        totalFailed={failedCount}
        vendors={vendorCount}
        assets={dashboardData?.activeAssets  || 0}
        vulns={vulns}
      />

      {/* ── Amalgamated posture (people · assets · governance · attack surface) ── */}
      <PillarGrid posture={posture} />

      {/* ── Framework workspaces (ISO 27001 ISMS) ────── */}
      <IsmsHomeCard />

      {/* ── Today's Focus ─────────────────────────────── */}
      {failedTests.length > 0 && (
        <section className="uh-focus">
          <div className="uh-section-hdr">
            <div>
              <div className="uh-section-title" style={{ marginBottom: 0 }}>{t('UserHome.todaysFocus')}</div>
              <p className="uh-focus-sub">{t('UserHome.todaysFocusSub')}</p>
            </div>
            <div className="uh-link-nav" onClick={() => navigate('/dashboard/tests')}>
              {t('UserHome.seeAllActionItems', { count: failedTests.length })} <i className="fas fa-arrow-right" />
            </div>
          </div>
          <div className="uh-focus-grid">
            {failedTests.slice(0, 3).map((test, i) => (
              <FocusCard key={i} test={test} />
            ))}
          </div>
        </section>
      )}

      {/* ── Framework Readiness ───────────────────────── */}
      {Object.keys(cd).length > 0 ? (
        <section>
          <div className="uh-section-hdr">
            <div className="uh-section-title" style={{ marginBottom: 0 }}>{t('UserHome.frameworkReadiness')}</div>
            <div className="uh-link-nav" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('UserHome.manageFrameworks')} <i className="fas fa-arrow-right" />
            </div>
          </div>
          <div className="uh-fw-section-grid">
            {Object.entries(cd).map(([name, pct]) => (
              <FrameworkBlock key={name} name={name} pct={pct} fwId={frameworkMap[name]} />
            ))}
          </div>
        </section>
      ) : (
        <div className="uh-section">
          <p className="uh-empty">{t('UserHome.noFrameworksEnrolled')}</p>
        </div>
      )}

      {/* ── Failing tests list + CCM ──────────────────── */}
      <div className="uh-bottom-2col">
        <div className="uh-section">
          <div className="uh-section-hdr" style={{ marginBottom: '0.625rem' }}>
            <div className="uh-section-title" style={{ marginBottom: 0 }}>
              {t('UserHome.failingTests')}
              {failedTests.length > 0 && <span className="uh-badge-red">{failedTests.length}</span>}
            </div>
            {failedTests.length > 0 && <span className="uh-meta-note">{t('UserHome.autoRetryHourly')}</span>}
          </div>
          {failedTests.length === 0 ? (
            <div className="uh-no-alerts">
              <i className="fas fa-check-circle" />
              <span>{t('UserHome.noFailingTestsGoodShape')}</span>
            </div>
          ) : (
            <>
              <ul className="uh-alert-list">
                {failedTests.slice(0, 8).map((test, i) => (
                  <li key={i} className="uh-alert-item" onClick={() => navigate(`/dashboard/tests/${test.id}`)}>
                    <span className="uh-alert-dot" />
                    <div className="uh-alert-info">
                      <span className="uh-alert-name">{test.testName}</span>
                      {test.failedDate && <span className="uh-alert-time">{timeAgo(test.failedDate)}</span>}
                    </div>
                    <i className="fas fa-chevron-right uh-alert-arrow" />
                  </li>
                ))}
              </ul>
              <div className="uh-link-nav" style={{ marginTop: '0.5rem' }} onClick={() => navigate('/dashboard/tests')}>
                {t('UserHome.viewAllFailingTests')} <i className="fas fa-arrow-right" />
              </div>
            </>
          )}
        </div>

        <div className="uh-section">
          <CcmPanel />
        </div>
      </div>

      <PendingSignaturesCard />

      {/* ── TSC Heatmap ───────────────────────────────── */}
      <div className="uh-section">
        <TscHeatmap />
      </div>
    </div>
  );
}

export default UserHome;
